/* eslint formatjs/no-literal-string-in-jsx: "error" */
import { useForm } from "react-hook-form"
import { HttpErrors } from "utils/HttpErrors"
import { reportError } from "utils/ErrorReporting"
import { Form } from "components/views/atoms/forms/Form"
import { Spacer } from "components/views/atoms/Atoms"
import React from "react"
import CarrotClient from "carrot-api"
import Settings from "utils/CarrotConfig"
import { useTrackView } from "../../../../utils/heap"
import { getErrorMessageFromCode } from "../../../../utils/CarrotErrors"
import { FormattedMessage, useIntl } from "react-intl"
import { CompleteFormInput } from "components/views/atoms/forms/FormInput"
import { StyledReadonlyWell, SwitchH2ToBodyLarge } from "../Molecules"
import { RegistrationFlowLayout } from "./RegistrationLayouts"
import { Stack, useTheme } from "@carrotfertility/carotene-core"
import { RegistrationButtonFooter, ContinueButton } from "./RegistrationButtons"

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

interface SetPasswordProps {
  email?: string
  onBack: () => void
  onComplete: (password: string) => void
}

export default function SetPassword({ email, onBack, onComplete }: SetPasswordProps): JSX.Element {
  const theme = useTheme()
  useTrackView("SetPasswordRegFlow", "Set password screen")
  const {
    register,
    formState: { errors },
    handleSubmit,
    formState: { isValid, isDirty }
  } = useForm({ mode: "onChange" })
  const intl = useIntl()

  const onSubmit = handleSubmit((formData) => {
    onComplete(formData.password)
  })

  const validatePassword = async (password) => {
    try {
      await client.validatePassword(password)
    } catch (error) {
      if (error.name === HttpErrors.BAD_REQUEST) {
        const { code } = await error.response.json()
        return intl.formatMessage(getErrorMessageFromCode(code))
      }
      reportError(error)
    }
    return true
  }

  return (
    <RegistrationFlowLayout
      header={<FormattedMessage defaultMessage="Here's your account email" />}
      description={
        <>
          <Spacer height={1} />
          <StyledReadonlyWell>{email}</StyledReadonlyWell>
          <Spacer height={2} />
          <SwitchH2ToBodyLarge>
            <FormattedMessage defaultMessage="Create a password" />
          </SwitchH2ToBodyLarge>
          <Spacer height={1} />
        </>
      }
      interactiveElements={
        <Form onSubmit={onSubmit}>
          <Stack spacing={theme.spacing(theme.tokens.spacing.xl)}>
            <CompleteFormInput
              {...register("password", { validate: validatePassword })}
              errors={errors}
              label={intl.formatMessage({ defaultMessage: "Password" })}
              type="password"
            />
            <RegistrationButtonFooter
              onBack={onBack}
              continueButton={
                <ContinueButton disabled={!isValid || !isDirty}>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
            />
          </Stack>
        </Form>
      }
    />
  )
}
